import { FormControl, FormGroup, FormLabel, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import { Control, Controller, FieldErrors, Path, UseFormRegister, UseFormReturn } from 'react-hook-form';
import MultiCheckbox from 'src/components/MultiCheckBox';
import { Unit, UnitUsedForCalculation } from 'src/entities/licenseEntity';
import { DesignatedTruckFeature } from 'src/entities/orderEntity';
import { TruckEntity, TruckDetailFields } from 'src/entities/truckEntity';

const options: (
  | 'パワーゲート'
  | '空調'
  | 'エアサス'
  | 'スタンション')[] = [
  'パワーゲート',
  '空調',
  'エアサス',
  'スタンション'
];

type Props<T extends TruckDetailFields> = {
  form: UseFormReturn<T>;
  control: Control<T>;
  register: UseFormRegister<T>;
  errors: FieldErrors<TruckEntity>
  unit: Unit;
  unitUsedForCalculation: UnitUsedForCalculation;
  features: DesignatedTruckFeature[];
  setFeatures: (features: DesignatedTruckFeature[]) => void;
};

function Details<T extends TruckDetailFields>({
  form,
  control,
  register,
  errors,
  unit,
  unitUsedForCalculation,
  features,
  setFeatures,
}: Props<T>) {
  return (
    <Stack gap={1}>
      <Stack direction="row" gap={1}>
        <TextField
          margin="dense"
          id="maximum_loading_capacity_kg"
          label={`最大積載量(${unit})`}
          type="text"
          fullWidth
          variant="standard"
          required
          {...register('maximum_loading_capacity_kg' as Path<T>)}
          error={'maximum_loading_capacity_kg' in errors}
          helperText={errors.maximum_loading_capacity_kg?.message}
          InputLabelProps={{ shrink: true }}
        />
        {
          unitUsedForCalculation === '重量と体積'
          && (
            <TextField
              margin="dense"
              id="loading_platform_volume_m3"
              label="最大積載量(m3)"
              type="text"
              fullWidth
              variant="standard"
              {...register('loading_platform_volume_m3' as Path<T>)}
              error={'loading_platform_volume_m3' in errors}
              helperText={errors.loading_platform_volume_m3?.message}
              InputLabelProps={{ shrink: true }}
            />
          )
        }
        <TextField
          margin="dense"
          id="maximum_loading_capacity_quantity"
          label="最大積載量(数量)"
          type="text"
          fullWidth
          variant="standard"
          {...register('maximum_loading_capacity_quantity' as Path<T>)}
          error={'maximum_loading_capacity_quantity' in errors}
          helperText={errors.maximum_loading_capacity_quantity?.message}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          margin="dense"
          id="max_num_pistons"
          label="最大回転数"
          type="text"
          fullWidth
          variant="standard"
          {...register('max_num_pistons' as Path<T>)}
          error={'max_num_pistons' in errors}
          helperText={errors.max_num_pistons?.message}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          margin="dense"
          id="max_num_locations_per_pistons"
          label="1回転あたりの最大納品先件数"
          type="text"
          fullWidth
          variant="standard"
          {...register('max_num_locations_per_pistons' as Path<T>)}
          error={'max_num_locations_per_pistons' in errors}
          helperText={errors.max_num_locations_per_pistons?.message}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          margin="dense"
          id="maximum_loading_rate"
          label="上限積載率"
          type="text"
          fullWidth
          variant="standard"
          {...register('maximum_loading_rate' as Path<T>)}
          error={'maximum_loading_rate' in errors}
          helperText={errors.maximum_loading_rate?.message}
          InputLabelProps={{ shrink: true }} // ← ここを追加
        />
        <TextField
          margin="dense"
          id="minimum_loading_rate"
          label="下限積載率"
          type="text"
          fullWidth
          variant="standard"
          {...register('minimum_loading_rate' as Path<T>)}
          error={'minimum_loading_rate' in errors}
          helperText={errors.minimum_loading_rate?.message}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>
      <Stack direction="row" gap={1}>
        <FormControl fullWidth>
          <InputLabel>車両タイプ</InputLabel>
          <Controller
            control={control}
            name={'klass' as Path<T>}
            render={({ field }) => (
              <Select
                id="klass"
                label="車両タイプ"
                type="text"
                variant="standard"
                {...field}
                value={field.value || ''}
              >
                <MenuItem value={null}>&nbsp;</MenuItem>
                <MenuItem value="軽貨物">軽貨物</MenuItem>
                <MenuItem value="2t">2t</MenuItem>
                <MenuItem value="4t">4t</MenuItem>
                <MenuItem value="10t">10t</MenuItem>
                <MenuItem value="20t">20t</MenuItem>
              </Select>
            )}
          />
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>車種</InputLabel>
          <Controller
            control={control}
            name={'car_model' as Path<T>}
            render={({ field }) => (
              <Select
                id="car_model"
                label="車種"
                type="text"
                variant="standard"
                {...field}
                value={field.value || ''}
              >
                <MenuItem value={null}>&nbsp;</MenuItem>
                <MenuItem value="平ボディ">平ボディ</MenuItem>
                <MenuItem value="バンボディ">バンボディ</MenuItem>
                <MenuItem value="ウィングボディ">ウィングボディ</MenuItem>
                <MenuItem value="保冷車">保冷車</MenuItem>
                <MenuItem value="冷凍車">冷凍車</MenuItem>
                <MenuItem value="車載車">車載車</MenuItem>
                <MenuItem value="重機運搬車">重機運搬車</MenuItem>
                <MenuItem value="危険物運搬車">危険物運搬車</MenuItem>
                <MenuItem value="ダンプ">ダンプ</MenuItem>
                <MenuItem value="幌">幌</MenuItem>
                <MenuItem value="ユニック">ユニック</MenuItem>
                <MenuItem value="海上コンテナー用">海上コンテナー用</MenuItem>
                <MenuItem value="その他">その他</MenuItem>
              </Select>
            )}
          />
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>床仕様</InputLabel>
          <Controller
            control={control}
            name={'floor_specification' as Path<T>}
            render={({ field }) => (
              <Select
                margin="dense"
                id="floor_specification"
                label="床仕様"
                type="text"
                fullWidth
                variant="standard"
                {...field}
                value={field.value || ''}
              >
                <MenuItem value={null}>&nbsp;</MenuItem>
                <MenuItem value="鉄板">鉄板</MenuItem>
                <MenuItem value="ステンレス">ステンレス</MenuItem>
                <MenuItem value="ジョルダー">ジョルダー</MenuItem>
                <MenuItem value="縞板">縞板</MenuItem>
              </Select>
            )}
          />
        </FormControl>
      </Stack>
      <Stack direction="row" gap={1}>
        <FormControl fullWidth>
          <InputLabel>荷台高さ</InputLabel>
          <Controller
            control={control}
            name={'loading_platform_height' as Path<T>}
            render={({ field }) => (
              <Select
                id="loading_platform_height"
                label="荷台高さ"
                type="text"
                fullWidth
                variant="standard"
                {...field}
                value={field.value || ''}
              >
                <MenuItem value={null}>&nbsp;</MenuItem>
                <MenuItem value="標準">標準</MenuItem>
                <MenuItem value="低床">低床</MenuItem>
                <MenuItem value="中低床">中低床</MenuItem>
              </Select>
            )}
          />
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>荷台幅</InputLabel>
          <Controller
            control={control}
            name={'loading_platform_width' as Path<T>}
            render={({ field }) => (
              <Select
                id="loading_platform_width"
                label="荷台幅"
                type="text"
                fullWidth
                variant="standard"
                {...field}
                value={field.value || ''}
              >
                <MenuItem value={null}>&nbsp;</MenuItem>
                <MenuItem value="標準">標準</MenuItem>
                <MenuItem value="セミワイド">セミワイド</MenuItem>
                <MenuItem value="ワイド">ワイド</MenuItem>
              </Select>
            )}
          />
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>荷台長さ</InputLabel>
          <Controller
            control={control}
            name={'loading_platform_length' as Path<T>}
            render={({ field }) => (
              <Select
                margin="dense"
                id="loading_platform_length"
                label="荷台長さ"
                type="text"
                fullWidth
                variant="standard"
                {...field}
                value={field.value || ''}
              >
                <MenuItem value={null}>&nbsp;</MenuItem>
                <MenuItem value="大型">大型</MenuItem>
                <MenuItem value="中型">中型</MenuItem>
                <MenuItem value="小型">小型</MenuItem>
                <MenuItem value="ショート">ショート</MenuItem>
                <MenuItem value="ロング">ロング</MenuItem>
              </Select>
            )}
          />
        </FormControl>
      </Stack>
      <Stack>
        <FormControl component="fieldset">
          <>
            <FormLabel component="legend">装置</FormLabel>
            <FormGroup row>
              <MultiCheckbox<T, DesignatedTruckFeature>
                form={form}
                control={control}
                name={'features' as Path<T>}
                options={options}
                defaultCheckedValues={features as string[] || []}
                valueSetter={setFeatures}
              />
            </FormGroup>
          </>
        </FormControl>
      </Stack>
      <Stack direction="row" gap={1}>
        <TextField
          margin="dense"
          id="truck_fuel_cost_yen_per_km"
          label="1kmあたりの燃料費"
          type="text"
          fullWidth
          variant="standard"
          {...register('truck_fuel_cost_yen_per_km' as Path<T>)}
          error={'truck_fuel_cost_yen_per_km' in errors}
          helperText={errors.truck_fuel_cost_yen_per_km?.message}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          margin="dense"
          id="truck_insurance_fee_yen_per_day"
          label="1日あたりの保険料"
          type="text"
          fullWidth
          variant="standard"
          {...register('truck_insurance_fee_yen_per_day' as Path<T>)}
          error={'truck_insurance_fee_yen_per_day' in errors}
          helperText={errors.truck_insurance_fee_yen_per_day?.message}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          margin="dense"
          id="truck_repair_cost_yen_per_day"
          label="1日あたりの修繕費"
          type="text"
          fullWidth
          variant="standard"
          {...register('truck_repair_cost_yen_per_day' as Path<T>)}
          error={'truck_repair_cost_yen_per_day' in errors}
          helperText={errors.truck_repair_cost_yen_per_day?.message}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          margin="dense"
          id="truck_expressway_fee_yen_per_shift"
          label="1日あたりの高速代"
          type="text"
          fullWidth
          variant="standard"
          {...register('truck_expressway_fee_yen_per_shift' as Path<T>)}
          error={'truck_expressway_fee_yen_per_shift' in errors}
          helperText={errors.truck_expressway_fee_yen_per_shift?.message}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>
    </Stack>
  );
}

export default Details;
